import React, { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useRBAC } from '@/lib/rbac';
import type { Permission } from '@/lib/rbac';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: Permission;
  roles?: string[];
}

const AccessDenied = ({ reason }: { reason: string }) => (
  <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-3 text-center">
    <AlertCircle className="h-10 w-10 text-rose-500" />
    <div>
      <p className="text-lg font-semibold text-[var(--text-heading)]">Access denied</p>
      <p className="text-[var(--text-secondary)]">{reason}</p>
    </div>
  </div>
);

export function ProtectedRoute({ children, permission, roles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, authResolved, user, currentTenant } = useAuth();
  const [location, setLocation] = useLocation();
  const rbac = useRBAC(user, currentTenant);

  // Always call useEffect at the top level, but conditionally execute logic inside
  React.useEffect(() => {
    if (authResolved && !isAuthenticated) {
      // Only redirect if we're not already on the login page
      if (!location.startsWith('/login')) {
        const encodedPath = encodeURIComponent(location);
        setLocation(`/login?next=${encodedPath}`);
      }
    }
  }, [authResolved, isAuthenticated, location, setLocation]);

  if (isLoading || !authResolved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-brand-red rounded-2xl flex items-center justify-center">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-brand-red rounded-full"></div>
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
              Loading Dashboard
            </h2>

            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin text-brand-red" />
              <span className="text-slate-600 dark:text-slate-300">
                Checking authentication...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Return null while the redirect is happening
    return null;
  }

  if (permission && !rbac.can(permission)) {
    return <AccessDenied reason={rbac.getDisabledReason(permission)} />;
  }

  if (roles && roles.length > 0 && !rbac.hasAnyRole(roles)) {
    return <AccessDenied reason="Your current role does not include access to this area." />;
  }

  return <>{children}</>;
}
